// Registry of Canyon Storm and Desert Storm plan variants.

import { rulebringersConfig } from './csRulebringers';
import {
  buildDawnbreakersConfig,
  DAWNBREAKERS_OWN_DEFAULT,
  type DawnbreakersOwnConfig,
  type DawnbreakersSide,
} from './csDawnbreakers';
import { desertstormConfig } from './desertstorm';
import { desertstormOneConfig } from './desertstormOne';
import type { CSPlanConfig, NonVirusSlot, PhaseDef, PlanSlotKey } from './types';
import type { DSPlanConfig, DSPhaseDef, DSNonSiloSlot, DSSlotKey } from './dsTypes';

export const DEFAULT_CS_VARIANT = 'rulebringers';
export const DEFAULT_DS_VARIANT = 'desertstorm';
export const DEFAULT_DAWNBREAKERS_SIDE: DawnbreakersSide = 'left';

const csStaticVariants: Record<string, CSPlanConfig> = {
  rulebringers: rulebringersConfig,
};

export const dsVariants: Record<string, DSPlanConfig> = {
  desertstorm: desertstormConfig,
  'desertstorm-one': desertstormOneConfig,
};

export const CS_VARIANT_KEYS = ['rulebringers', 'dawnbreakers'] as const;
export type CSVariantKey = (typeof CS_VARIANT_KEYS)[number];

export function isDawnbreakersSide(v: string | null | undefined): v is DawnbreakersSide {
  return v === 'left' || v === 'right';
}

// Custom-variant registry — populated at runtime from the server. See
// `usePlanVariants` in `hooks/usePlanVariants.ts`. Values are the raw
// server DTOs (config is the editable subset — phases[].capacities,
// phases[].strengthClaims, homeByRank, ptCoreRanks/coreRanks).
//
// Resolvers below merge custom overrides on top of the seed they were
// derived from, so priorities/phase copy/slot notes stay in sync with
// the seed even when only the numeric parts were edited.
export type CustomVariantEntry = {
  key: string;
  name: string;
  basedOn: string;
  config: unknown;
};

type Registry = { ds: CustomVariantEntry[]; cs: CustomVariantEntry[] };
let customRegistry: Registry = { ds: [], cs: [] };
let overrideDefaults: { ds?: string; cs?: string } = {};
let registryVersion = 0;
const registryListeners = new Set<() => void>();

export function setCustomPlanRegistry(next: Registry, defaults: { ds?: string; cs?: string }) {
  customRegistry = next;
  overrideDefaults = defaults;
  registryVersion++;
  registryListeners.forEach((l) => l());
}

export function getCustomPlanRegistry(): Registry {
  return customRegistry;
}

export function getPlanDefault(kind: 'ds' | 'cs'): string {
  if (kind === 'ds') return overrideDefaults.ds ?? DEFAULT_DS_VARIANT;
  return overrideDefaults.cs ?? DEFAULT_CS_VARIANT;
}

// External-store hooks so React components re-render when the registry
// hydrates or a mutation invalidates it.
export function subscribePlanRegistry(listener: () => void): () => void {
  registryListeners.add(listener);
  return () => {
    registryListeners.delete(listener);
  };
}

export function getPlanRegistryVersion(): number {
  return registryVersion;
}

type DSEditableConfig = {
  phases: Array<{
    capacities: Record<DSSlotKey, number>;
    strengthClaims?: Array<{ slot: DSSlotKey; count: number }>;
  }>;
  homeByRank: DSNonSiloSlot[];
  coreRanks: readonly number[];
};

type CSEditableConfig = {
  phases: Array<{
    capacities: Record<PlanSlotKey, number>;
    strengthClaims?: Array<{ slot: PlanSlotKey; count: number }>;
  }>;
  homeByRank: NonVirusSlot[];
  ptCoreRanks: readonly number[];
};

export function extractDSEditable(cfg: DSPlanConfig): DSEditableConfig {
  return {
    phases: cfg.phases.map((p) => ({
      capacities: { ...p.capacities },
      strengthClaims: p.strengthClaims?.map((c) => ({ ...c })),
    })),
    homeByRank: [...cfg.homeByRank],
    coreRanks: [...cfg.coreRanks],
  };
}

export function extractCSEditable(cfg: CSPlanConfig): CSEditableConfig {
  return {
    phases: cfg.phases.map((p) => ({
      capacities: { ...p.capacities },
      strengthClaims: p.strengthClaims?.map((c) => ({ ...c })),
    })),
    homeByRank: [...cfg.homeByRank],
    ptCoreRanks: [...cfg.ptCoreRanks],
  };
}

export type DawnbreakersEditable = DawnbreakersOwnConfig;

export function extractDawnbreakersEditable(): DawnbreakersEditable {
  return {
    phases: DAWNBREAKERS_OWN_DEFAULT.phases.map((p) => ({
      capacities: { ...p.capacities },
      strengthClaims: p.strengthClaims?.map((c) => ({ ...c })),
    })),
    homeByRank: [...DAWNBREAKERS_OWN_DEFAULT.homeByRank],
    ptCoreRanks: [...DAWNBREAKERS_OWN_DEFAULT.ptCoreRanks],
  };
}

function seedDSConfig(basedOn: string): DSPlanConfig {
  return dsVariants[basedOn] ?? dsVariants[DEFAULT_DS_VARIANT];
}

function seedCSConfig(basedOn: string, side: DawnbreakersSide): CSPlanConfig {
  if (basedOn === 'dawnbreakers') return buildDawnbreakersConfig(side);
  return csStaticVariants[basedOn] ?? csStaticVariants[DEFAULT_CS_VARIANT];
}

function mergeDSOverride(
  seed: DSPlanConfig,
  override: DSEditableConfig,
  meta: { key: string; name: string },
): DSPlanConfig {
  return {
    ...seed,
    meta: { ...seed.meta, key: meta.key, name: meta.name },
    phases: seed.phases.map((phase, i) => {
      const ov = override.phases[i];
      if (!ov) return phase;
      return {
        ...phase,
        capacities: { ...phase.capacities, ...ov.capacities },
        strengthClaims: ov.strengthClaims ?? phase.strengthClaims,
      } satisfies DSPhaseDef;
    }),
    homeByRank: override.homeByRank,
    coreRanks: override.coreRanks,
  };
}

function mergeCSOverride(
  seed: CSPlanConfig,
  override: CSEditableConfig,
  meta: { key: string; name: string },
): CSPlanConfig {
  return {
    ...seed,
    meta: { ...seed.meta, key: meta.key, name: meta.name },
    phases: seed.phases.map((phase, i) => {
      const ov = override.phases[i];
      if (!ov) return phase;
      return {
        ...phase,
        capacities: { ...phase.capacities, ...ov.capacities },
        strengthClaims: ov.strengthClaims ?? phase.strengthClaims,
      } satisfies PhaseDef;
    }),
    homeByRank: override.homeByRank,
    ptCoreRanks: override.ptCoreRanks,
  };
}

export function resolveCSVariant(
  key: string | null | undefined,
  side?: string | null,
): CSPlanConfig {
  const s = isDawnbreakersSide(side) ? side : DEFAULT_DAWNBREAKERS_SIDE;
  const effectiveKey = key ?? getPlanDefault('cs');

  const custom = customRegistry.cs.find((v) => v.key === effectiveKey);
  if (custom) {
    if (custom.basedOn === 'dawnbreakers') {
      return buildDawnbreakersConfig(s, custom.config as DawnbreakersOwnConfig);
    }
    const seed = seedCSConfig(custom.basedOn, s);
    return mergeCSOverride(seed, custom.config as CSEditableConfig, {
      key: custom.key,
      name: custom.name,
    });
  }

  if (effectiveKey === 'dawnbreakers') return buildDawnbreakersConfig(s);
  if (csStaticVariants[effectiveKey]) return csStaticVariants[effectiveKey];
  return csStaticVariants[DEFAULT_CS_VARIANT];
}

export function resolveDSVariant(key: string | null | undefined): DSPlanConfig {
  const effectiveKey = key ?? getPlanDefault('ds');
  const custom = customRegistry.ds.find((v) => v.key === effectiveKey);
  if (custom) {
    const seed = seedDSConfig(custom.basedOn);
    return mergeDSOverride(seed, custom.config as DSEditableConfig, {
      key: custom.key,
      name: custom.name,
    });
  }
  if (dsVariants[effectiveKey]) return dsVariants[effectiveKey];
  return dsVariants[DEFAULT_DS_VARIANT];
}

export type { DawnbreakersSide } from './csDawnbreakers';
export type { CSPlanConfig } from './types';
export type { DSPlanConfig } from './dsTypes';
export * from './types';
export * from './dsTypes';

export type { DawnbreakersOwnConfig, OwnSideSlotKey, OwnSideNonVirusSlot } from './csDawnbreakers';
export { OWN_SLOT_KEYS, OWN_NON_VIRUS_SLOTS } from './csDawnbreakers';
